import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Formik, Form, Field, FieldArray } from 'formik';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Grid from 'apollo-react/components/Grid';
import Button from 'apollo-react/components/Button';
import Radio from 'apollo-react/components/Radio';
import RadioGroup from 'apollo-react/components/RadioGroup';
import { toast } from 'react-toastify';
import HcpSignupPageFrame from './HcpSignupPageFrame';
import { FormikTextField, FormikSelect } from '../common/FormikComponent';
import MenuItem from 'apollo-react/components/MenuItem';
import {
  addressCountryOptions,
  accreditationStatusOptions,
  educationExperienceConfigDefaults,
  formatHcpFileUploadHint,
  buildHcpSignupWizardAttachmentFileConfig,
  getHcpSignupWizardAttachmentFileValidationOpts,
  hcpSignupFormFieldVariant,
} from './hcpSignupDefaults';
import { buildEducationExperienceSchema } from './educationExperienceSchema';
import {
  getSignupDraft,
  postEducationExperienceDraft,
  postSignupComplete,
  HCP_SIGNUP_DRAFT_STORAGE_KEY,
} from './hcpSignupApi';
import {
  isHcpSignupSubmitted,
  shouldRedirectForward,
  deriveResumeSignupPath,
  HCP_SIGNUP_NAVIGATE_BACK_STATE,
} from './hcpSignupFlowUtils';
import { useDebouncedValidDraftSave } from './useDebouncedValidDraftSave';
import { normalizeWizardDateInput } from './hcpSignupDateUtils';
import FileFieldInput from '../common/FormCrafterFormRenderer/inputs/fields/FileFieldInput';
import {
  HCP_SIGNUP_FIELD_HINTS,
  HcpSignupHintIcon,
  HcpSignupLabelWithHint,
  HcpSignupNativeDateLabel,
} from './HcpSignupFieldLabel';
import { useLicensingExperienceOptional } from '../../contexts/LicensingExperienceContext';

const YES_NO_VALUES = new Set(['yes', 'no']);

function shouldShowSignupFieldError(meta, revealErrors) {
  return Boolean(meta?.error && (meta?.touched || revealErrors));
}

function showNestedSignupError(touchedFlag, errorMsg, revealErrors) {
  return Boolean(errorMsg && (touchedFlag || revealErrors));
}

function setWizardDateField(f, field, rawValue) {
  const iso = normalizeWizardDateInput(rawValue);
  f.setFieldValue(field.name, iso);
  f.setFieldTouched(field.name, true, false);
}

/** Map API fields to yes/no for radio UI; legacy free text becomes "yes". */
function experienceFieldToYesNo(raw) {
  const s = String(raw ?? '')
    .trim()
    .toLowerCase();
  if (YES_NO_VALUES.has(s)) return s;
  if (!String(raw ?? '').trim()) return '';
  return 'yes';
}

function HcpSignupYesNoRadioGroup({
  name,
  label,
  required,
  row = true,
  revealErrors = false,
}) {
  /** Apollo InputLabel’s required asterisk is absolutely positioned and sits on the text; use HcpSignupLabelWithHint `showRequiredAfterText` like other wizard fields. */
  const labelNode =
    required && React.isValidElement(label)
      ? React.cloneElement(label, { showRequiredAfterText: true })
      : label;

  return (
    <Field name={name}>
      {({ field, meta }) => (
        <RadioGroup
          aria-label={name}
          aria-required={required || undefined}
          name={field.name}
          value={field.value != null && field.value !== '' ? field.value : ''}
          onChange={field.onChange}
          onBlur={field.onBlur}
          label={labelNode}
          required={false}
          row={row}
          error={shouldShowSignupFieldError(meta, revealErrors)}
          helperText={
            shouldShowSignupFieldError(meta, revealErrors)
              ? meta.error
              : undefined
          }
        >
          <Radio value="yes" label="Yes" />
          <Radio value="no" label="No" />
        </RadioGroup>
      )}
    </Field>
  );
}

function EducationAutoSaveWatcher({
  draftId,
  formik,
  setInitial,
  setRelAttachMeta,
}) {
  const { values, dirty, isSubmitting, validateForm, setErrors } = formik;
  const valuesRef = useRef(values);
  valuesRef.current = values;

  const persistQuiet = useCallback(async () => {
    if (!draftId) return;
    try {
      const res = await postEducationExperienceDraft(
        draftId,
        valuesRef.current
      );
      if (!res.success) {
        if (res.errors && typeof res.errors === 'object')
          applyServerValidationErrors(res.errors, setErrors);
        return;
      }
      const ex = responseExperienceToForm(res.data?.experience);
      const nextEduc = draftEducationsToRows(res.data?.educations);
      setInitial({ educations: nextEduc, experience: ex });
      setRelAttachMeta(
        Array.isArray(res.data?.experience?.relevantAttachments)
          ? res.data.experience.relevantAttachments.map((a) => ({
              id: a.id,
              originalName: a.originalName || `File ${a.id}`,
            }))
          : []
      );
    } catch (e) {
      const payload = e.response?.data;
      if (payload?.errors && typeof payload.errors === 'object')
        applyServerValidationErrors(payload.errors, setErrors);
    }
  }, [draftId, setErrors, setInitial, setRelAttachMeta]);

  useDebouncedValidDraftSave({
    enabled: Boolean(draftId),
    dirty,
    isSubmitting,
    valuesWatch: values,
    validateForm,
    save: persistQuiet,
  });

  return null;
}

function newRowKey() {
  return `k-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
}

function createEmptyEducationRow() {
  return {
    _rowKey: newRowKey(),
    countryOfCertificate: '',
    degreeName: '',
    institutionName: '',
    authorityName: '',
    fieldOfStudy: '',
    graduationDate: '',
    studyYears: '',
    accreditationStatus: '',
    accreditingAuthority: '',
    accreditationFile: null,
    existingAccreditationDocumentUploadId: null,
    accreditationDocumentLabel: '',
  };
}

/** @param {object} e */
function apiEducationToRow(e) {
  return {
    _rowKey: newRowKey(),
    countryOfCertificate: e.countryOfCertificate || '',
    degreeName: e.degreeName || '',
    institutionName: e.institutionName || '',
    authorityName: e.authorityName || '',
    fieldOfStudy: e.fieldOfStudy || '',
    graduationDate: e.graduationDate
      ? String(e.graduationDate).slice(0, 10)
      : '',
    studyYears: e.studyYears != null ? e.studyYears : '',
    accreditationStatus: e.accreditationStatus || '',
    accreditingAuthority: e.accreditingAuthority || '',
    accreditationFile: null,
    existingAccreditationDocumentUploadId: e.accreditationDocument?.id ?? null,
    accreditationDocumentLabel: e.accreditationDocument?.originalName
      ? `Uploaded: ${e.accreditationDocument.originalName}`
      : '',
  };
}

/** @param {object[]|undefined} list */
function draftEducationsToRows(list) {
  if (!Array.isArray(list) || !list.length) return [createEmptyEducationRow()];
  return list.map(apiEducationToRow);
}

function defaultExperience() {
  return {
    internshipTraining: '',
    postGraduationInternship: '',
    internshipCompletion: '',
    internshipStart: '',
    internshipEnd: '',
    internshipCertificateFile: null,
    existingInternshipCertificateUploadId: null,
    internshipCertificateLabel: '',
    additionalExperience: '',
    additionalExperienceStart: '',
    additionalExperienceEnd: '',
    additionalExperienceCertificateFile: null,
    existingAdditionalExperienceCertificateUploadId: null,
    additionalExperienceCertificateLabel: '',
    undergraduateInternship: '',
    undergraduateInternshipStart: '',
    undergraduateInternshipEnd: '',
    undergraduateInternshipCertificateFile: null,
    existingUndergraduateInternshipCertificateUploadId: null,
    undergraduateInternshipCertificateLabel: '',
    relevantAttachmentFiles: [],
    existingRelevantAttachmentIds: [],
  };
}

/** @param {object|null|undefined} exp */
function responseExperienceToForm(exp) {
  if (!exp) return defaultExperience();
  return {
    internshipTraining: exp.internshipTraining || '',
    postGraduationInternship: experienceFieldToYesNo(
      exp.postGraduationInternship
    ),
    internshipCompletion: experienceFieldToYesNo(exp.internshipCompletion),
    internshipStart: exp.internshipStart
      ? String(exp.internshipStart).slice(0, 10)
      : '',
    internshipEnd: exp.internshipEnd
      ? String(exp.internshipEnd).slice(0, 10)
      : '',
    internshipCertificateFile: null,
    existingInternshipCertificateUploadId:
      exp.internshipCertificate?.id ?? null,
    internshipCertificateLabel: exp.internshipCertificate?.originalName
      ? `Uploaded: ${exp.internshipCertificate.originalName}`
      : '',
    additionalExperience: experienceFieldToYesNo(exp.additionalExperience),
    additionalExperienceStart: exp.additionalExperienceStart
      ? String(exp.additionalExperienceStart).slice(0, 10)
      : '',
    additionalExperienceEnd: exp.additionalExperienceEnd
      ? String(exp.additionalExperienceEnd).slice(0, 10)
      : '',
    additionalExperienceCertificateFile: null,
    existingAdditionalExperienceCertificateUploadId:
      exp.additionalExperienceCertificate?.id ?? null,
    additionalExperienceCertificateLabel: exp.additionalExperienceCertificate
      ?.originalName
      ? `Uploaded: ${exp.additionalExperienceCertificate.originalName}`
      : '',
    undergraduateInternship: exp.undergraduateInternship || '',
    undergraduateInternshipStart: exp.undergraduateInternshipStart
      ? String(exp.undergraduateInternshipStart).slice(0, 10)
      : '',
    undergraduateInternshipEnd: exp.undergraduateInternshipEnd
      ? String(exp.undergraduateInternshipEnd).slice(0, 10)
      : '',
    undergraduateInternshipCertificateFile: null,
    existingUndergraduateInternshipCertificateUploadId:
      exp.undergraduateInternshipCertificate?.id ?? null,
    undergraduateInternshipCertificateLabel: exp
      .undergraduateInternshipCertificate?.originalName
      ? `Uploaded: ${exp.undergraduateInternshipCertificate.originalName}`
      : '',
    relevantAttachmentFiles: [],
    existingRelevantAttachmentIds: Array.isArray(exp.relevantAttachmentIds)
      ? [...exp.relevantAttachmentIds]
      : [],
  };
}

/** Strip legacy "Uploaded:" prefix for FileList display (same pattern as license attachment). */
function accreditationDocDisplayName(row) {
  const raw = row?.accreditationDocumentLabel;
  if (!raw) return '';
  return String(raw)
    .replace(/^Uploaded:\s*/i, '')
    .trim();
}

function additionalExpCertDisplayName(experience) {
  const raw = experience?.additionalExperienceCertificateLabel;
  if (!raw) return '';
  return String(raw)
    .replace(/^Uploaded:\s*/i, '')
    .replace(/^Selected:\s*/i, '')
    .trim();
}

function internshipCertDisplayName(experience) {
  const raw = experience?.internshipCertificateLabel;
  if (!raw) return '';
  return String(raw)
    .replace(/^Uploaded:\s*/i, '')
    .replace(/^Selected:\s*/i, '')
    .trim();
}

function undergraduateInternshipCertDisplayName(experience) {
  const raw = experience?.undergraduateInternshipCertificateLabel;
  if (!raw) return '';
  return String(raw)
    .replace(/^Uploaded:\s*/i, '')
    .replace(/^Selected:\s*/i, '')
    .trim();
}

function mapServerEducationErrors(educationsErrors) {
  if (!Array.isArray(educationsErrors)) return educationsErrors;
  return educationsErrors.map((e) => {
    if (!e || typeof e !== 'object') return e;
    const o = { ...e };
    if (o.accreditationDocument) {
      o.accreditationFile = o.accreditationDocument;
      delete o.accreditationDocument;
    }
    return o;
  });
}

function mapServerExperienceErrors(err) {
  if (!err || typeof err !== 'object') return err;
  const o = { ...err };
  if (o.internshipCertificate) {
    o.internshipCertificateFile = o.internshipCertificate;
    delete o.internshipCertificate;
  }
  if (o.additionalExperienceCertificate) {
    o.additionalExperienceCertificateFile = o.additionalExperienceCertificate;
    delete o.additionalExperienceCertificate;
  }
  if (o.undergraduateInternshipCertificate) {
    o.undergraduateInternshipCertificateFile =
      o.undergraduateInternshipCertificate;
    delete o.undergraduateInternshipCertificate;
  }
  delete o.educations;
  return o;
}

/** @param {object} values */
function touchAllShape(values) {
  const edu = (values.educations || []).map(() => ({
    countryOfCertificate: true,
    degreeName: true,
    institutionName: true,
    authorityName: true,
    fieldOfStudy: true,
    graduationDate: true,
    studyYears: true,
    accreditationStatus: true,
    accreditingAuthority: true,
    accreditationFile: true,
  }));
  return {
    educations: edu,
    experience: {
      internshipTraining: true,
      postGraduationInternship: true,
      internshipCompletion: true,
      internshipStart: true,
      internshipEnd: true,
      internshipCertificateFile: true,
      existingInternshipCertificateUploadId: true,
      additionalExperience: true,
      additionalExperienceStart: true,
      additionalExperienceEnd: true,
      additionalExperienceCertificateFile: true,
      existingAdditionalExperienceCertificateUploadId: true,
      undergraduateInternship: true,
      undergraduateInternshipStart: true,
      undergraduateInternshipEnd: true,
      undergraduateInternshipCertificateFile: true,
      existingUndergraduateInternshipCertificateUploadId: true,
      relevantAttachmentFiles: true,
      existingRelevantAttachmentIds: true,
    },
  };
}

function applyServerValidationErrors(errors, setErrors) {
  /** @type {Record<string, unknown>} */
  const next = {};
  if (errors.educations)
    next.educations = mapServerEducationErrors(errors.educations);
  const { educations: _e, ...rest } = errors;
  if (rest && Object.keys(rest).length > 0) {
    next.experience = mapServerExperienceErrors(rest);
  }
  setErrors(next);
}

export default function HcpSignupEducationExperienceStep() {
  const navigate = useNavigate();
  const location = useLocation();
  const navigateBackChosen = Boolean(location.state?.hcpSignupNavigateBack);
  const { mode } = useLicensingExperienceOptional();
  const licenseTrackingRelaxed = mode === 'license_tracking';

  useEffect(() => {
    if (licenseTrackingRelaxed) {
      navigate('/hcp-signup/step-1', { replace: true });
    }
  }, [licenseTrackingRelaxed, navigate]);

  const validationSchema = useMemo(
    () => buildEducationExperienceSchema({}, { licenseTrackingRelaxed }),
    [licenseTrackingRelaxed]
  );

  const accreditationDocFileConfig = useMemo(
    () => buildHcpSignupWizardAttachmentFileConfig(1),
    []
  );

  const accreditationDocUploadHint = useMemo(() => {
    const o = getHcpSignupWizardAttachmentFileValidationOpts();
    return formatHcpFileUploadHint(o.acceptExt, o.maxMb, true);
  }, []);

  const relevantAttachFileConfig = useMemo(() => {
    const exp = educationExperienceConfigDefaults.experience;
    return buildHcpSignupWizardAttachmentFileConfig(exp.relevantAttachmentsMax);
  }, []);

  const relevantAttachUploadHint = useMemo(() => {
    const exp = educationExperienceConfigDefaults.experience;
    const o = getHcpSignupWizardAttachmentFileValidationOpts();
    const line = formatHcpFileUploadHint(o.acceptExt, o.maxMb, false);
    return `${line} Up to ${exp.relevantAttachmentsMax} files.`;
  }, []);

  const internshipCertFileConfig = useMemo(
    () => buildHcpSignupWizardAttachmentFileConfig(1),
    []
  );

  const internshipCertUploadHint = useMemo(() => {
    const o = getHcpSignupWizardAttachmentFileValidationOpts();
    return formatHcpFileUploadHint(o.acceptExt, o.maxMb, false);
  }, []);

  const [draftId, setDraftId] = useState(null);
  const [initial, setInitial] = useState({
    educations: [createEmptyEducationRow()],
    experience: defaultExperience(),
  });
  const [ready, setReady] = useState(false);
  const [completedPublicId, setCompletedPublicId] = useState(null);
  const [revealValidationErrors, setRevealValidationErrors] = useState(false);
  const [relAttachMeta, setRelAttachMeta] = useState([]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      const rawId = window.localStorage.getItem(HCP_SIGNUP_DRAFT_STORAGE_KEY);
      const id = rawId ? parseInt(rawId, 10) : NaN;

      if (Number.isNaN(id)) {
        if (!cancel) {
          toast.error('Complete Step 1 first.');
          navigate('/hcp-signup/step-1');
        }
        return;
      }

      try {
        const res = await getSignupDraft(id);
        if (cancel) return;
        if (!res?.success || !res.data?.draftId) {
          toast.error('No sign-up draft found.');
          navigate('/hcp-signup/step-1');
          return;
        }

        const d = res.data;

        if (shouldRedirectForward(3, d, navigateBackChosen)) {
          navigate(deriveResumeSignupPath(d), { replace: true });
          return;
        }

        if (
          !licenseTrackingRelaxed &&
          Array.isArray(d.licenses) &&
          d.licenses.length === 0 &&
          !isHcpSignupSubmitted(d) &&
          !d.hcpProfile?.publicHcpId
        ) {
          toast.error('Complete Step 2 (licenses) first.');
          navigate('/hcp-signup/step-2');
          return;
        }

        const pid = d.hcpProfile?.publicHcpId ?? null;
        if (pid || isHcpSignupSubmitted(d)) {
          if (!cancel) {
            setCompletedPublicId(pid || '—');
            setDraftId(d.draftId);
            setReady(true);
          }
          return;
        }

        const exp = d.experience
          ? responseExperienceToForm(d.experience)
          : defaultExperience();

        setDraftId(d.draftId);
        setInitial({
          educations: draftEducationsToRows(d.educations),
          experience: exp,
        });
        setRelAttachMeta(
          Array.isArray(d.experience?.relevantAttachments)
            ? d.experience.relevantAttachments.map((a) => ({
                id: a.id,
                originalName: a.originalName || `File ${a.id}`,
              }))
            : []
        );
      } catch {
        toast.error('Could not load draft.');
        navigate('/hcp-signup/step-2');
      } finally {
        if (!cancel) setReady(true);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [navigate, navigateBackChosen, licenseTrackingRelaxed]);

  const handleFinish = useCallback(
    async (values, formikHelpers) => {
      const { setSubmitting, setErrors, validateForm, setTouched } =
        formikHelpers;
      setRevealValidationErrors(true);
      setTouched(touchAllShape(values));
      const formErrors = await validateForm();
      if (formErrors && Object.keys(formErrors).length > 0) {
        setErrors(formErrors);
        toast.error('Please fix the highlighted fields before finishing.');
        setSubmitting(false);
        return;
      }

      if (!draftId) return;

      try {
        setSubmitting(true);
        const saved = await postEducationExperienceDraft(draftId, values);
        if (!saved.success) {
          if (saved.errors && typeof saved.errors === 'object')
            applyServerValidationErrors(saved.errors, setErrors);
          setRevealValidationErrors(true);
          toast.error(saved.message || 'Could not save before finishing.');
          return;
        }

        const done = await postSignupComplete(draftId);

        if (!done.success) {
          toast.error(done.message || 'Could not complete sign-up');
          return;
        }

        const pid = done.data?.publicHcpId;
        if (pid) setCompletedPublicId(pid);
        toast.success('Your HCP profile has been created.');
      } catch (e) {
        const payload = e.response?.data;
        setRevealValidationErrors(true);
        if (payload?.errors && typeof payload.errors === 'object') {
          applyServerValidationErrors(payload.errors, formikHelpers.setErrors);
        }
        toast.error(payload?.message || 'Finish failed.');
      } finally {
        setSubmitting(false);
      }
    },
    [draftId]
  );

  if (!ready) {
    return (
      <HcpSignupPageFrame>
        <Grid container justifyContent="center" className="signInTitleGrid">
          <p className="signInDesc">Loading…</p>
        </Grid>
      </HcpSignupPageFrame>
    );
  }

  if (completedPublicId) {
    return (
      <HcpSignupPageFrame>
        <Grid container justifyContent="center" className="signInTitleGrid">
          <div className="authCompletionPanel">
            <h2 className="authCompletionTitle">Sign-up submitted</h2>
            <p className="authCompletionBody">Your unique HCP ID:</p>
            <p className="authCompletionId">{completedPublicId}</p>
            <p
              className="authFormHint"
              style={{ textAlign: 'center', marginTop: 16 }}
            >
              Keep this ID for reference. Supporting documents may still be
              verified before activation.
            </p>
            <Link to="/login" className="authCompletionLink">
              Continue to login
            </Link>
          </div>
        </Grid>
      </HcpSignupPageFrame>
    );
  }

  return (
    <HcpSignupPageFrame>
      <Grid container justifyContent="center" className="signInTitleGrid">
        <div>
          <h2 className="signInTitle">
            HCP sign-up — education &amp; experience
          </h2>
          <p className="signInDesc">
            Step 3 of 3. Add qualifications and experience. Your progress is
            saved automatically once the form is valid. Use{' '}
            <strong>Finish sign-up</strong> when you are ready to create your
            profile and receive your public HCP ID.
          </p>
          <button
            type="button"
            className="authWizardBack"
            onClick={() =>
              navigate('/hcp-signup/step-2', {
                state: HCP_SIGNUP_NAVIGATE_BACK_STATE,
              })
            }
          >
            ← Back to licenses
          </button>
        </div>
      </Grid>

      <Grid container className="formContainer">
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            <Formik
              key={`${draftId}-edu`}
              initialValues={initial}
              enableReinitialize
              validationSchema={validationSchema}
              validateOnChange
              validateOnBlur
              onSubmit={() => {}}
            >
              {(formik) => {
                const { values, errors, touched, setFieldValue, isSubmitting } =
                  formik;

                const trainingYes =
                  values.experience?.internshipTraining === 'yes';

                return (
                  <Form
                    noValidate
                    onSubmit={(e) => {
                      e.preventDefault();
                    }}
                  >
                    <EducationAutoSaveWatcher
                      draftId={draftId}
                      formik={formik}
                      setInitial={setInitial}
                      setRelAttachMeta={setRelAttachMeta}
                    />
                    <p className="authFormSectionHeading">Education</p>
                    <span className="authFormHint">
                      Add each qualification separately. Accreditation fields
                      apply when status is accredited.
                    </span>

                    <FieldArray name="educations">
                      {(arrayHelpers) => (
                        <>
                          {values.educations.map((row, index) => {
                            const base = `educations.${index}`;
                            const rowErrors = errors.educations?.[index];
                            const rowTouched = touched.educations?.[index];
                            const accredited =
                              row.accreditationStatus === 'accredited';

                            return (
                              <React.Fragment key={row._rowKey || index}>
                                {index > 0 ? (
                                  <hr className="authFormDivider" />
                                ) : null}
                                <div>
                                  <p className="authQualificationTitle">
                                    Qualification {index + 1}
                                  </p>
                                  <Grid
                                    container
                                    rowSpacing={0}
                                    columnSpacing={2}
                                    className="authWizardFieldsGrid"
                                  >
                                    <Grid item xs={6}>
                                      <FormikSelect
                                        name={`${base}.countryOfCertificate`}
                                        label={
                                          <HcpSignupLabelWithHint
                                            text="Country of certificate"
                                            hint={
                                              HCP_SIGNUP_FIELD_HINTS.eduCountryOfCertificate
                                            }
                                          />
                                        }
                                        fullWidth
                                        required
                                        variant={hcpSignupFormFieldVariant}
                                      >
                                        <MenuItem value="" disabled>
                                          <em>Select country</em>
                                        </MenuItem>
                                        {addressCountryOptions.map((o) => (
                                          <MenuItem
                                            key={o.value}
                                            value={o.value}
                                          >
                                            {o.label}
                                          </MenuItem>
                                        ))}
                                      </FormikSelect>
                                    </Grid>

                                    <Grid item xs={6}>
                                      <FormikTextField
                                        name={`${base}.degreeName`}
                                        label={
                                          <HcpSignupLabelWithHint
                                            text="Degree name"
                                            hint={
                                              HCP_SIGNUP_FIELD_HINTS.eduDegreeName
                                            }
                                          />
                                        }
                                        required
                                        fullWidth
                                        variant={hcpSignupFormFieldVariant}
                                      />
                                    </Grid>

                                    <Grid item xs={6}>
                                      <FormikTextField
                                        name={`${base}.institutionName`}
                                        label={
                                          <HcpSignupLabelWithHint
                                            text="Institution name"
                                            hint={
                                              HCP_SIGNUP_FIELD_HINTS.eduInstitutionName
                                            }
                                          />
                                        }
                                        required
                                        fullWidth
                                        variant={hcpSignupFormFieldVariant}
                                      />
                                    </Grid>

                                    <Grid item xs={6}>
                                      <FormikTextField
                                        name={`${base}.authorityName`}
                                        label={
                                          <HcpSignupLabelWithHint
                                            text="Authority name"
                                            hint={
                                              HCP_SIGNUP_FIELD_HINTS.eduAuthorityName
                                            }
                                          />
                                        }
                                        required
                                        fullWidth
                                        variant={hcpSignupFormFieldVariant}
                                      />
                                    </Grid>

                                    <Grid item xs={12}>
                                      <FormikTextField
                                        name={`${base}.fieldOfStudy`}
                                        label={
                                          <HcpSignupLabelWithHint
                                            text="Field of study"
                                            hint={
                                              HCP_SIGNUP_FIELD_HINTS.eduFieldOfStudy
                                            }
                                          />
                                        }
                                        required
                                        fullWidth
                                        variant={hcpSignupFormFieldVariant}
                                      />
                                    </Grid>

                                    <Grid item xs={6}>
                                      <Field name={`${base}.graduationDate`}>
                                        {({ field, meta, form: f }) => (
                                          <div className="authNativeDateWrap">
                                            <HcpSignupNativeDateLabel
                                              htmlFor={`${base}-grad`}
                                              text="Graduation date"
                                              hint={
                                                HCP_SIGNUP_FIELD_HINTS.eduGraduationDate
                                              }
                                              required
                                            />
                                            <input
                                              id={`${base}-grad`}
                                              type="date"
                                              className="authNativeDateInput"
                                              name={field.name}
                                              value={field.value}
                                              onBlur={field.onBlur}
                                              onChange={(e) => {
                                                setWizardDateField(
                                                  f,
                                                  field,
                                                  e.target.value
                                                );
                                              }}
                                            />
                                            {shouldShowSignupFieldError(
                                              meta,
                                              revealValidationErrors
                                            ) ? (
                                              <span className="authFieldError">
                                                {meta.error}
                                              </span>
                                            ) : null}
                                          </div>
                                        )}
                                      </Field>
                                    </Grid>

                                    <Grid item xs={6}>
                                      <FormikTextField
                                        name={`${base}.studyYears`}
                                        label={
                                          <HcpSignupLabelWithHint
                                            text="Study years (1–10)"
                                            hint={
                                              HCP_SIGNUP_FIELD_HINTS.eduStudyYears
                                            }
                                          />
                                        }
                                        required
                                        fullWidth
                                        variant={hcpSignupFormFieldVariant}
                                        type="number"
                                        inputProps={{
                                          min: 1,
                                          max: 10,
                                          step: 1,
                                        }}
                                      />
                                    </Grid>

                                    <Grid item xs={6}>
                                      <FormikSelect
                                        name={`${base}.accreditationStatus`}
                                        label={
                                          <HcpSignupLabelWithHint
                                            text="Accreditation status"
                                            hint={
                                              HCP_SIGNUP_FIELD_HINTS.eduAccreditationStatus
                                            }
                                          />
                                        }
                                        fullWidth
                                        required
                                        variant={hcpSignupFormFieldVariant}
                                      >
                                        <MenuItem value="" disabled>
                                          <em>Select status</em>
                                        </MenuItem>
                                        {accreditationStatusOptions.map((o) => (
                                          <MenuItem
                                            key={o.value}
                                            value={o.value}
                                          >
                                            {o.label}
                                          </MenuItem>
                                        ))}
                                      </FormikSelect>
                                    </Grid>

                                    {accredited ? (
                                      <Grid item xs={6}>
                                        <FormikTextField
                                          name={`${base}.accreditingAuthority`}
                                          label={
                                            <HcpSignupLabelWithHint
                                              text="Accrediting authority"
                                              hint={
                                                HCP_SIGNUP_FIELD_HINTS.eduAccreditingAuthority
                                              }
                                            />
                                          }
                                          required
                                          fullWidth
                                          variant={hcpSignupFormFieldVariant}
                                        />
                                      </Grid>
                                    ) : null}

                                    <Grid item xs={12}>
                                      {accredited ? (
                                        <FileFieldInput
                                          id={`hcp_signup_accreditation_${row._rowKey}`}
                                          label={
                                            <HcpSignupLabelWithHint
                                              text="Accreditation document"
                                              hint={
                                                HCP_SIGNUP_FIELD_HINTS.eduAccreditationDocument
                                              }
                                              showRequiredAfterText
                                            />
                                          }
                                          columnStyle={{
                                            width: '100%',
                                            maxWidth: '100%',
                                          }}
                                          value={
                                            row.accreditationFile instanceof
                                            File
                                              ? [row.accreditationFile]
                                              : []
                                          }
                                          onUpload={(uploaded) => {
                                            const list = Array.isArray(uploaded)
                                              ? uploaded
                                              : [uploaded];
                                            const next = list.find(
                                              (x) => x instanceof File
                                            );
                                            if (!next) return;
                                            setFieldValue(
                                              `${base}.accreditationFile`,
                                              next
                                            );
                                            setFieldValue(
                                              `${base}.existingAccreditationDocumentUploadId`,
                                              null
                                            );
                                            setFieldValue(
                                              `${base}.accreditationDocumentLabel`,
                                              next.name
                                            );
                                          }}
                                          onDelete={() => {
                                            setFieldValue(
                                              `${base}.accreditationFile`,
                                              null
                                            );
                                            setFieldValue(
                                              `${base}.accreditationDocumentLabel`,
                                              ''
                                            );
                                          }}
                                          onServerFileDelete={async () => {
                                            setFieldValue(
                                              `${base}.existingAccreditationDocumentUploadId`,
                                              null
                                            );
                                            setFieldValue(
                                              `${base}.accreditationDocumentLabel`,
                                              ''
                                            );
                                          }}
                                          helperText={
                                            showNestedSignupError(
                                              rowTouched?.accreditationFile,
                                              rowErrors?.accreditationFile,
                                              revealValidationErrors
                                            )
                                              ? rowErrors.accreditationFile
                                              : accreditationDocUploadHint
                                          }
                                          showError={showNestedSignupError(
                                            rowTouched?.accreditationFile,
                                            rowErrors?.accreditationFile,
                                            revealValidationErrors
                                          )}
                                          disabled={false}
                                          required={false}
                                          fileConfig={
                                            accreditationDocFileConfig
                                          }
                                          existingFiles={
                                            row.existingAccreditationDocumentUploadId &&
                                            !(
                                              row.accreditationFile instanceof
                                              File
                                            )
                                              ? [
                                                  {
                                                    id: row.existingAccreditationDocumentUploadId,
                                                    name:
                                                      accreditationDocDisplayName(
                                                        row
                                                      ) ||
                                                      `Document #${row.existingAccreditationDocumentUploadId}`,
                                                    mimeType: '',
                                                    size: 0,
                                                  },
                                                ]
                                              : []
                                          }
                                          eagerUploadEnabled={false}
                                          enablePendingDelete={false}
                                          initialStagedFiles={[]}
                                        />
                                      ) : null}
                                    </Grid>

                                    <Grid
                                      item
                                      xs={12}
                                      className="authFormRowActions"
                                    >
                                      <Button
                                        type="button"
                                        variant="secondary"
                                        className="authWizardSecondaryButton"
                                        disabled={values.educations.length <= 1}
                                        onClick={() =>
                                          arrayHelpers.remove(index)
                                        }
                                      >
                                        Remove qualification
                                      </Button>
                                    </Grid>
                                  </Grid>
                                </div>
                              </React.Fragment>
                            );
                          })}

                          <Button
                            type="button"
                            variant="secondary"
                            className="authWizardSecondaryButton"
                            onClick={() =>
                              arrayHelpers.push(createEmptyEducationRow())
                            }
                          >
                            Add another qualification
                          </Button>
                        </>
                      )}
                    </FieldArray>

                    <hr className="authFormDivider" />

                    <p className="authFormSectionHeading">
                      Professional experience
                    </p>

                    <Grid
                      container
                      rowSpacing={0}
                      columnSpacing={2}
                      className="authWizardFieldsGrid"
                    >
                      <Grid item xs={12}>
                        <HcpSignupYesNoRadioGroup
                          name="experience.internshipTraining"
                          label={
                            <HcpSignupLabelWithHint
                              text="Internship / training"
                              hint={
                                HCP_SIGNUP_FIELD_HINTS.expInternshipTraining
                              }
                            />
                          }
                          required={
                            educationExperienceConfigDefaults.experience
                              .internshipTrainingRequired
                          }
                          revealErrors={revealValidationErrors}
                        />
                      </Grid>

                      {trainingYes ? (
                        <>
                          <Grid item xs={12}>
                            <HcpSignupYesNoRadioGroup
                              name="experience.postGraduationInternship"
                              label={
                                <HcpSignupLabelWithHint
                                  text="Post-graduation internship"
                                  hint={
                                    HCP_SIGNUP_FIELD_HINTS.expPostGradInternship
                                  }
                                />
                              }
                              required={
                                educationExperienceConfigDefaults.experience
                                  .postGradInternRequired
                              }
                              revealErrors={revealValidationErrors}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <HcpSignupYesNoRadioGroup
                              name="experience.internshipCompletion"
                              label={
                                <HcpSignupLabelWithHint
                                  text="Internship completion"
                                  hint={
                                    HCP_SIGNUP_FIELD_HINTS.expInternshipCompletion
                                  }
                                />
                              }
                              required={
                                educationExperienceConfigDefaults.experience
                                  .internshipCompletionRequired
                              }
                              revealErrors={revealValidationErrors}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <span
                              className="authFormHint"
                              style={{ display: 'block', marginBottom: 4 }}
                            >
                              <span
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 4,
                                }}
                              >
                                Internship dates
                                <HcpSignupHintIcon
                                  hint={
                                    HCP_SIGNUP_FIELD_HINTS.expInternshipDates
                                  }
                                />
                              </span>
                            </span>
                            <Grid
                              container
                              columnSpacing={2}
                              rowSpacing={1}
                              sx={{ width: '100%' }}
                            >
                              <Grid item xs={12} sm={6}>
                                <Field name="experience.internshipStart">
                                  {({ field, meta, form: f }) => (
                                    <div
                                      className="authNativeDateWrap"
                                      style={{ marginTop: 0 }}
                                    >
                                      <HcpSignupNativeDateLabel
                                        htmlFor="internship-start"
                                        text="Start"
                                        hint={
                                          HCP_SIGNUP_FIELD_HINTS.expInternshipStart
                                        }
                                        required
                                      />
                                      <input
                                        id="internship-start"
                                        type="date"
                                        className="authNativeDateInput"
                                        name={field.name}
                                        value={field.value}
                                        onBlur={field.onBlur}
                                        onChange={(e) => {
                                          setWizardDateField(
                                            f,
                                            field,
                                            e.target.value
                                          );
                                        }}
                                      />
                                      {shouldShowSignupFieldError(
                                        meta,
                                        revealValidationErrors
                                      ) ? (
                                        <span className="authFieldError">
                                          {meta.error}
                                        </span>
                                      ) : null}
                                    </div>
                                  )}
                                </Field>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Field name="experience.internshipEnd">
                                  {({ field, meta, form: f }) => (
                                    <div className="authNativeDateWrap">
                                      <HcpSignupNativeDateLabel
                                        htmlFor="internship-end"
                                        text="End"
                                        hint={
                                          HCP_SIGNUP_FIELD_HINTS.expInternshipEnd
                                        }
                                        required
                                      />
                                      <input
                                        id="internship-end"
                                        type="date"
                                        className="authNativeDateInput"
                                        name={field.name}
                                        value={field.value}
                                        onBlur={field.onBlur}
                                        onChange={(e) => {
                                          setWizardDateField(
                                            f,
                                            field,
                                            e.target.value
                                          );
                                        }}
                                      />
                                      {shouldShowSignupFieldError(
                                        meta,
                                        revealValidationErrors
                                      ) ? (
                                        <span className="authFieldError">
                                          {meta.error}
                                        </span>
                                      ) : null}
                                    </div>
                                  )}
                                </Field>
                              </Grid>
                            </Grid>
                          </Grid>

                          <Grid item xs={12}>
                            <FileFieldInput
                              id="hcp_signup_internship_certificate"
                              label={
                                <HcpSignupLabelWithHint
                                  text="Internship completion certificate"
                                  hint={
                                    HCP_SIGNUP_FIELD_HINTS.expInternshipCertificate
                                  }
                                  showRequiredAfterText={
                                    values.experience.internshipCompletion ===
                                      'yes' &&
                                    educationExperienceConfigDefaults.experience
                                      .internshipCertificateRequiredWhenInternshipCompletionYes
                                  }
                                />
                              }
                              columnStyle={{ width: '100%', maxWidth: '100%' }}
                              value={
                                values.experience
                                  .internshipCertificateFile instanceof File
                                  ? [
                                      values.experience
                                        .internshipCertificateFile,
                                    ]
                                  : []
                              }
                              onUpload={(uploaded) => {
                                const list = Array.isArray(uploaded)
                                  ? uploaded
                                  : [uploaded];
                                const next = list.find(
                                  (x) => x instanceof File
                                );
                                if (!next) return;
                                setFieldValue(
                                  'experience.internshipCertificateFile',
                                  next
                                );
                                setFieldValue(
                                  'experience.existingInternshipCertificateUploadId',
                                  null
                                );
                                setFieldValue(
                                  'experience.internshipCertificateLabel',
                                  next.name
                                );
                              }}
                              onDelete={() => {
                                setFieldValue(
                                  'experience.internshipCertificateFile',
                                  null
                                );
                                setFieldValue(
                                  'experience.internshipCertificateLabel',
                                  ''
                                );
                              }}
                              onServerFileDelete={async () => {
                                setFieldValue(
                                  'experience.existingInternshipCertificateUploadId',
                                  null
                                );
                                setFieldValue(
                                  'experience.internshipCertificateLabel',
                                  ''
                                );
                              }}
                              helperText={
                                showNestedSignupError(
                                  touched.experience?.internshipCertificateFile,
                                  errors.experience?.internshipCertificateFile,
                                  revealValidationErrors
                                )
                                  ? errors.experience.internshipCertificateFile
                                  : internshipCertUploadHint
                              }
                              showError={showNestedSignupError(
                                touched.experience?.internshipCertificateFile,
                                errors.experience?.internshipCertificateFile,
                                revealValidationErrors
                              )}
                              disabled={false}
                              required={false}
                              fileConfig={internshipCertFileConfig}
                              existingFiles={
                                values.experience
                                  .existingInternshipCertificateUploadId &&
                                !(
                                  values.experience
                                    .internshipCertificateFile instanceof File
                                )
                                  ? [
                                      {
                                        id: values.experience
                                          .existingInternshipCertificateUploadId,
                                        name:
                                          internshipCertDisplayName(
                                            values.experience
                                          ) ||
                                          `Document #${values.experience.existingInternshipCertificateUploadId}`,
                                        mimeType: '',
                                        size: 0,
                                      },
                                    ]
                                  : []
                              }
                              eagerUploadEnabled={false}
                              enablePendingDelete={false}
                              initialStagedFiles={[]}
                            />
                          </Grid>

                          {educationExperienceConfigDefaults.experience
                            .additionalExperienceSectionEnabled ? (
                            <>
                              <Grid item xs={12}>
                                <HcpSignupYesNoRadioGroup
                                  name="experience.additionalExperience"
                                  label={
                                    <HcpSignupLabelWithHint
                                      text="Additional experience"
                                      hint={
                                        HCP_SIGNUP_FIELD_HINTS.expAdditionalExperience
                                      }
                                    />
                                  }
                                  required={
                                    educationExperienceConfigDefaults.experience
                                      .additionalExperienceRequiredWhenInternshipYes
                                  }
                                  revealErrors={revealValidationErrors}
                                />
                              </Grid>
                              {values.experience.additionalExperience ===
                              'yes' ? (
                                <>
                                  <Grid item xs={12}>
                                    <span
                                      className="authFormHint"
                                      style={{
                                        display: 'block',
                                        marginBottom: 4,
                                      }}
                                    >
                                      <span
                                        style={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: 4,
                                        }}
                                      >
                                        Additional experience dates
                                        <HcpSignupHintIcon
                                          hint={
                                            HCP_SIGNUP_FIELD_HINTS.expAdditionalExperienceDates
                                          }
                                        />
                                      </span>
                                    </span>
                                    <Grid
                                      container
                                      columnSpacing={2}
                                      rowSpacing={1}
                                      sx={{ width: '100%' }}
                                    >
                                      <Grid item xs={12} sm={6}>
                                        <Field name="experience.additionalExperienceStart">
                                          {({ field, meta, form: f }) => (
                                            <div
                                              className="authNativeDateWrap"
                                              style={{ marginTop: 0 }}
                                            >
                                              <HcpSignupNativeDateLabel
                                                htmlFor="additional-exp-start"
                                                text="Start"
                                                hint={
                                                  HCP_SIGNUP_FIELD_HINTS.expInternshipStart
                                                }
                                                required
                                              />
                                              <input
                                                id="additional-exp-start"
                                                type="date"
                                                className="authNativeDateInput"
                                                name={field.name}
                                                value={field.value}
                                                onBlur={field.onBlur}
                                                onChange={(e) => {
                                                  setWizardDateField(
                                                    f,
                                                    field,
                                                    e.target.value
                                                  );
                                                }}
                                              />
                                              {shouldShowSignupFieldError(
                                                meta,
                                                revealValidationErrors
                                              ) ? (
                                                <span className="authFieldError">
                                                  {meta.error}
                                                </span>
                                              ) : null}
                                            </div>
                                          )}
                                        </Field>
                                      </Grid>
                                      <Grid item xs={12} sm={6}>
                                        <Field name="experience.additionalExperienceEnd">
                                          {({ field, meta, form: f }) => (
                                            <div className="authNativeDateWrap">
                                              <HcpSignupNativeDateLabel
                                                htmlFor="additional-exp-end"
                                                text="End"
                                                hint={
                                                  HCP_SIGNUP_FIELD_HINTS.expInternshipEnd
                                                }
                                                required
                                              />
                                              <input
                                                id="additional-exp-end"
                                                type="date"
                                                className="authNativeDateInput"
                                                name={field.name}
                                                value={field.value}
                                                onBlur={field.onBlur}
                                                onChange={(e) => {
                                                  setWizardDateField(
                                                    f,
                                                    field,
                                                    e.target.value
                                                  );
                                                }}
                                              />
                                              {shouldShowSignupFieldError(
                                                meta,
                                                revealValidationErrors
                                              ) ? (
                                                <span className="authFieldError">
                                                  {meta.error}
                                                </span>
                                              ) : null}
                                            </div>
                                          )}
                                        </Field>
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                  <Grid item xs={12}>
                                    <FileFieldInput
                                      id="hcp_signup_additional_experience_certificate"
                                      label={
                                        <HcpSignupLabelWithHint
                                          text="Additional experience certificate"
                                          hint={
                                            HCP_SIGNUP_FIELD_HINTS.expAdditionalExperienceCertificate
                                          }
                                          showRequiredAfterText
                                        />
                                      }
                                      columnStyle={{
                                        width: '100%',
                                        maxWidth: '100%',
                                      }}
                                      value={
                                        values.experience
                                          .additionalExperienceCertificateFile instanceof
                                        File
                                          ? [
                                              values.experience
                                                .additionalExperienceCertificateFile,
                                            ]
                                          : []
                                      }
                                      onUpload={(uploaded) => {
                                        const list = Array.isArray(uploaded)
                                          ? uploaded
                                          : [uploaded];
                                        const next = list.find(
                                          (x) => x instanceof File
                                        );
                                        if (!next) return;
                                        setFieldValue(
                                          'experience.additionalExperienceCertificateFile',
                                          next
                                        );
                                        setFieldValue(
                                          'experience.existingAdditionalExperienceCertificateUploadId',
                                          null
                                        );
                                        setFieldValue(
                                          'experience.additionalExperienceCertificateLabel',
                                          next.name
                                        );
                                      }}
                                      onDelete={() => {
                                        setFieldValue(
                                          'experience.additionalExperienceCertificateFile',
                                          null
                                        );
                                        setFieldValue(
                                          'experience.additionalExperienceCertificateLabel',
                                          ''
                                        );
                                      }}
                                      onServerFileDelete={async () => {
                                        setFieldValue(
                                          'experience.existingAdditionalExperienceCertificateUploadId',
                                          null
                                        );
                                        setFieldValue(
                                          'experience.additionalExperienceCertificateLabel',
                                          ''
                                        );
                                      }}
                                      helperText={
                                        showNestedSignupError(
                                          touched.experience
                                            ?.additionalExperienceCertificateFile,
                                          errors.experience
                                            ?.additionalExperienceCertificateFile,
                                          revealValidationErrors
                                        )
                                          ? errors.experience
                                              .additionalExperienceCertificateFile
                                          : internshipCertUploadHint
                                      }
                                      showError={showNestedSignupError(
                                        touched.experience
                                          ?.additionalExperienceCertificateFile,
                                        errors.experience
                                          ?.additionalExperienceCertificateFile,
                                        revealValidationErrors
                                      )}
                                      disabled={false}
                                      required={false}
                                      fileConfig={internshipCertFileConfig}
                                      existingFiles={
                                        values.experience
                                          .existingAdditionalExperienceCertificateUploadId &&
                                        !(
                                          values.experience
                                            .additionalExperienceCertificateFile instanceof
                                          File
                                        )
                                          ? [
                                              {
                                                id: values.experience
                                                  .existingAdditionalExperienceCertificateUploadId,
                                                name:
                                                  additionalExpCertDisplayName(
                                                    values.experience
                                                  ) ||
                                                  `Document #${values.experience.existingAdditionalExperienceCertificateUploadId}`,
                                                mimeType: '',
                                                size: 0,
                                              },
                                            ]
                                          : []
                                      }
                                      eagerUploadEnabled={false}
                                      enablePendingDelete={false}
                                      initialStagedFiles={[]}
                                    />
                                  </Grid>
                                </>
                              ) : null}
                            </>
                          ) : null}
                        </>
                      ) : null}

                      <Grid item xs={12}>
                        <FormikTextField
                          name="experience.undergraduateInternship"
                          label={
                            <HcpSignupLabelWithHint
                              text="Undergraduate internship"
                              hint={
                                HCP_SIGNUP_FIELD_HINTS.expUndergraduateInternship
                              }
                            />
                          }
                          fullWidth
                          variant={hcpSignupFormFieldVariant}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <span
                          className="authFormHint"
                          style={{ display: 'block', marginBottom: 4 }}
                        >
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                            }}
                          >
                            Undergraduate internship dates
                            <HcpSignupHintIcon
                              hint={
                                HCP_SIGNUP_FIELD_HINTS.expUndergraduateInternshipStart
                              }
                            />
                          </span>
                        </span>
                        <Grid
                          container
                          columnSpacing={2}
                          rowSpacing={1}
                          sx={{ width: '100%' }}
                        >
                          <Grid item xs={12} sm={6}>
                            <Field name="experience.undergraduateInternshipStart">
                              {({ field, meta, form: f }) => (
                                <div
                                  className="authNativeDateWrap"
                                  style={{ marginTop: 0 }}
                                >
                                  <HcpSignupNativeDateLabel
                                    htmlFor="undergraduate-internship-start"
                                    text="Start"
                                    hint={
                                      HCP_SIGNUP_FIELD_HINTS.expUndergraduateInternshipStart
                                    }
                                  />
                                  <input
                                    id="undergraduate-internship-start"
                                    type="date"
                                    className="authNativeDateInput"
                                    name={field.name}
                                    value={field.value}
                                    onBlur={field.onBlur}
                                    onChange={(e) => {
                                      setWizardDateField(
                                        f,
                                        field,
                                        e.target.value
                                      );
                                    }}
                                  />
                                  {shouldShowSignupFieldError(
                                    meta,
                                    revealValidationErrors
                                  ) ? (
                                    <span className="authFieldError">
                                      {meta.error}
                                    </span>
                                  ) : null}
                                </div>
                              )}
                            </Field>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Field name="experience.undergraduateInternshipEnd">
                              {({ field, meta, form: f }) => (
                                <div className="authNativeDateWrap">
                                  <HcpSignupNativeDateLabel
                                    htmlFor="undergraduate-internship-end"
                                    text="End"
                                    hint={
                                      HCP_SIGNUP_FIELD_HINTS.expUndergraduateInternshipEnd
                                    }
                                  />
                                  <input
                                    id="undergraduate-internship-end"
                                    type="date"
                                    className="authNativeDateInput"
                                    name={field.name}
                                    value={field.value}
                                    onBlur={field.onBlur}
                                    onChange={(e) => {
                                      setWizardDateField(
                                        f,
                                        field,
                                        e.target.value
                                      );
                                    }}
                                  />
                                  {shouldShowSignupFieldError(
                                    meta,
                                    revealValidationErrors
                                  ) ? (
                                    <span className="authFieldError">
                                      {meta.error}
                                    </span>
                                  ) : null}
                                </div>
                              )}
                            </Field>
                          </Grid>
                        </Grid>
                      </Grid>

                      <Grid item xs={12}>
                        <FileFieldInput
                          id="hcp_signup_undergraduate_internship_certificate"
                          label={
                            <HcpSignupLabelWithHint
                              text="Undergraduate internship certificate"
                              hint={
                                HCP_SIGNUP_FIELD_HINTS.expUndergraduateInternshipCertificate
                              }
                              showRequiredAfterText={
                                educationExperienceConfigDefaults.experience
                                  .undergraduateInternCertificateRequired
                              }
                            />
                          }
                          columnStyle={{ width: '100%', maxWidth: '100%' }}
                          value={
                            values.experience
                              .undergraduateInternshipCertificateFile instanceof
                            File
                              ? [
                                  values.experience
                                    .undergraduateInternshipCertificateFile,
                                ]
                              : []
                          }
                          onUpload={(uploaded) => {
                            const list = Array.isArray(uploaded)
                              ? uploaded
                              : [uploaded];
                            const next = list.find((x) => x instanceof File);
                            if (!next) return;
                            setFieldValue(
                              'experience.undergraduateInternshipCertificateFile',
                              next
                            );
                            setFieldValue(
                              'experience.existingUndergraduateInternshipCertificateUploadId',
                              null
                            );
                            setFieldValue(
                              'experience.undergraduateInternshipCertificateLabel',
                              next.name
                            );
                          }}
                          onDelete={() => {
                            setFieldValue(
                              'experience.undergraduateInternshipCertificateFile',
                              null
                            );
                            setFieldValue(
                              'experience.undergraduateInternshipCertificateLabel',
                              ''
                            );
                          }}
                          onServerFileDelete={async () => {
                            setFieldValue(
                              'experience.existingUndergraduateInternshipCertificateUploadId',
                              null
                            );
                            setFieldValue(
                              'experience.undergraduateInternshipCertificateLabel',
                              ''
                            );
                          }}
                          helperText={
                            showNestedSignupError(
                              touched.experience
                                ?.undergraduateInternshipCertificateFile,
                              errors.experience
                                ?.undergraduateInternshipCertificateFile,
                              revealValidationErrors
                            )
                              ? errors.experience
                                  .undergraduateInternshipCertificateFile
                              : internshipCertUploadHint
                          }
                          showError={showNestedSignupError(
                            touched.experience
                              ?.undergraduateInternshipCertificateFile,
                            errors.experience
                              ?.undergraduateInternshipCertificateFile,
                            revealValidationErrors
                          )}
                          disabled={false}
                          required={false}
                          fileConfig={internshipCertFileConfig}
                          existingFiles={
                            values.experience
                              .existingUndergraduateInternshipCertificateUploadId &&
                            !(
                              values.experience
                                .undergraduateInternshipCertificateFile instanceof
                              File
                            )
                              ? [
                                  {
                                    id: values.experience
                                      .existingUndergraduateInternshipCertificateUploadId,
                                    name:
                                      undergraduateInternshipCertDisplayName(
                                        values.experience
                                      ) ||
                                      `Document #${values.experience.existingUndergraduateInternshipCertificateUploadId}`,
                                    mimeType: '',
                                    size: 0,
                                  },
                                ]
                              : []
                          }
                          eagerUploadEnabled={false}
                          enablePendingDelete={false}
                          initialStagedFiles={[]}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <FileFieldInput
                          id="hcp_signup_relevant_attachments"
                          label={
                            <HcpSignupLabelWithHint
                              text="Relevant attachments (optional)"
                              hint={
                                HCP_SIGNUP_FIELD_HINTS.expRelevantAttachments
                              }
                            />
                          }
                          columnStyle={{ width: '100%', maxWidth: '100%' }}
                          value={(
                            values.experience.relevantAttachmentFiles || []
                          ).filter((f) => f instanceof File)}
                          onUpload={(uploaded) => {
                            const list = (
                              Array.isArray(uploaded) ? uploaded : [uploaded]
                            ).filter((x) => x instanceof File);
                            const cur =
                              values.experience.relevantAttachmentFiles || [];
                            setFieldValue(
                              'experience.relevantAttachmentFiles',
                              [...cur, ...list]
                            );
                          }}
                          onDelete={(file) => {
                            const cur =
                              values.experience.relevantAttachmentFiles || [];
                            setFieldValue(
                              'experience.relevantAttachmentFiles',
                              cur.filter((f) => f !== file)
                            );
                          }}
                          onServerFileDelete={async (ids) => {
                            const idList = Array.isArray(ids)
                              ? ids
                              : ids != null
                                ? [ids]
                                : [];
                            setRelAttachMeta((m) =>
                              m.filter((x) => !idList.includes(x.id))
                            );
                            setFieldValue(
                              'experience.existingRelevantAttachmentIds',
                              (
                                values.experience
                                  .existingRelevantAttachmentIds || []
                              ).filter((x) => !idList.includes(x))
                            );
                          }}
                          helperText={
                            showNestedSignupError(
                              touched.experience?.relevantAttachmentFiles,
                              errors.experience?.relevantAttachmentFiles,
                              revealValidationErrors
                            )
                              ? errors.experience.relevantAttachmentFiles
                              : relevantAttachUploadHint
                          }
                          showError={showNestedSignupError(
                            touched.experience?.relevantAttachmentFiles,
                            errors.experience?.relevantAttachmentFiles,
                            revealValidationErrors
                          )}
                          disabled={false}
                          required={false}
                          fileConfig={relevantAttachFileConfig}
                          existingFiles={relAttachMeta.map((a) => ({
                            id: a.id,
                            name: a.originalName,
                            mimeType: '',
                            size: 0,
                          }))}
                          eagerUploadEnabled={false}
                          enablePendingDelete={false}
                          initialStagedFiles={[]}
                        />
                      </Grid>
                    </Grid>

                    <Button
                      variant="primary"
                      type="button"
                      className="loginButton"
                      disabled={isSubmitting}
                      onClick={() => handleFinish(values, formik)}
                    >
                      Finish sign-up — create profile
                    </Button>
                  </Form>
                );
              }}
            </Formik>
          </Grid>
        </Grid>
      </Grid>

      <Grid container justifyContent="center">
        <p className="signUpText">
          Already have an account?{' '}
          <a href="/login" className="signUpLink">
            Sign In
          </a>
        </p>
      </Grid>
    </HcpSignupPageFrame>
  );
}
